// Inspired by the tokenizer approach in safe-expr-eval: https://github.com/alecasg555/safe-expr-eval

export type TokenType =
  | "NUMBER"
  | "IDENTIFIER"
  | "OPERATOR"
  | "PAREN_OPEN"
  | "PAREN_CLOSE"
  | "COMMA"
  | "EOF";

export interface Token {
  type: TokenType;
  value: string | number;
  position: number;
}

export class Tokenizer {
  private input: string;
  private pos: number;

  constructor(input: string) {
    this.input = input;
    this.pos = 0;
  }

  tokenize(): Token[] {
    const tokens: Token[] = [];
    while (this.pos < this.input.length) {
      this.skipWhitespace();
      if (this.pos >= this.input.length) {
        break;
      }

      const ch = this.input[this.pos];
      const startPos = this.pos;

      if (
        this.isDigit(ch) ||
        (ch === "." && this.isDigit(this.input[this.pos + 1] ?? ""))
      ) {
        tokens.push(this.readNumber(startPos));
      } else if (this.isLetter(ch) || ch === "_") {
        tokens.push(this.readIdentifier(startPos));
      } else if (ch === "(") {
        tokens.push({ type: "PAREN_OPEN", value: "(", position: startPos });
        this.pos++;
      } else if (ch === ")") {
        tokens.push({ type: "PAREN_CLOSE", value: ")", position: startPos });
        this.pos++;
      } else if (ch === ",") {
        tokens.push({ type: "COMMA", value: ",", position: startPos });
        this.pos++;
      } else if (this.isOperator(ch)) {
        tokens.push({ type: "OPERATOR", value: ch, position: startPos });
        this.pos++;
      } else {
        throw new Error(`Unexpected character '${ch}' at position ${startPos}`);
      }
    }
    tokens.push({ type: "EOF", value: "", position: this.pos });
    return tokens;
  }

  private skipWhitespace(): void {
    while (this.pos < this.input.length && /\s/.test(this.input[this.pos])) {
      this.pos++;
    }
  }

  private isDigit(ch: string): boolean {
    return ch >= "0" && ch <= "9";
  }

  private isLetter(ch: string): boolean {
    return (ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z");
  }

  private isOperator(ch: string): boolean {
    return (
      ch === "+" ||
      ch === "-" ||
      ch === "*" ||
      ch === "/" ||
      ch === "^" ||
      ch === "%"
    );
  }

  private readNumber(startPos: number): Token {
    let numStr = "";
    while (
      this.pos < this.input.length &&
      (this.isDigit(this.input[this.pos]) || this.input[this.pos] === ".")
    ) {
      numStr += this.input[this.pos];
      this.pos++;
    }
    // Handle scientific notation (e.g., 1e10, 2.5e-3)
    if (
      this.pos < this.input.length &&
      (this.input[this.pos] === "e" || this.input[this.pos] === "E")
    ) {
      numStr += this.input[this.pos];
      this.pos++;
      if (
        this.pos < this.input.length &&
        (this.input[this.pos] === "+" || this.input[this.pos] === "-")
      ) {
        numStr += this.input[this.pos];
        this.pos++;
      }
      while (
        this.pos < this.input.length &&
        this.isDigit(this.input[this.pos])
      ) {
        numStr += this.input[this.pos];
        this.pos++;
      }
    }
    const value = Number(numStr);
    if (Number.isNaN(value)) {
      throw new Error(`Invalid number '${numStr}' at position ${startPos}`);
    }
    return { type: "NUMBER", value, position: startPos };
  }

  private readIdentifier(startPos: number): Token {
    let name = "";
    while (
      this.pos < this.input.length &&
      (this.isLetter(this.input[this.pos]) ||
        this.isDigit(this.input[this.pos]) ||
        this.input[this.pos] === "_")
    ) {
      name += this.input[this.pos];
      this.pos++;
    }
    return { type: "IDENTIFIER", value: name, position: startPos };
  }
}
